'use client';

/**
 * <AnalyticsProvider> — Root component that wires analytics into a Next.js
 * app. Mount inside <ConsentProvider> so the consent context is available.
 *
 * Behavior:
 * - If POSTHOG_KEY env is unset → renders children, captures are NO-OPs.
 * - If consent.analytics === false → renders children, captures are NO-OPs.
 * - If consent.analytics === true → lazily initializes PostHog, fires
 *   `page_viewed` on mount, then defers to caller for downstream events.
 *
 * Wrap pages in <AnalyticsProvider appName="..."> after <ConsentProvider>.
 */

import React, { useEffect } from 'react';
import { useAnalytics } from './consent';
import type { AnalyticsAppName } from './types';

interface AnalyticsProviderProps {
  appName: AnalyticsAppName;
  /** Optional tenant_id to tag every event from this provider scope. */
  tenantId?: string;
  children: React.ReactNode;
}

export function AnalyticsProvider({ appName, tenantId, children }: AnalyticsProviderProps): React.JSX.Element {
  const { capture, enabled } = useAnalytics(appName);

  useEffect(() => {
    // Fire on every mount where consent is granted. Page-view granularity
    // can be increased via per-route capture calls in apps that need it.
    if (!enabled) return;
    capture('page_viewed', {
      tenant_id: tenantId,
      path: typeof window !== 'undefined' ? window.location.pathname : undefined,
    });
  }, [enabled, capture, tenantId]);

  return <>{children}</>;
}
